import React, { Component } from 'react';
import { reaction } from 'mobx';
import { observer } from 'mobx-react';
import { TOOL_CALL_STATUS } from '@kentik/ui-shared/kaizen/constants';
import getErrorBoundary from 'core/util/getErrorBoundary';
import { createToolResultComponent, isSubAgentTool } from './ToolResultFactory';
import SubAgentRunPanel from './components/SubAgentRunPanel';
import { isSameToolCall } from './util/turnStepGroups';
import { KaizenToolResultPanelWrapper } from './KaizenChatSession.styles';

// make an error boundary for the tool result panel so if one of the tools fails to render, it doesn't break the whole page
const ErrorBoundaryCmp = getErrorBoundary('KaizenToolResultPanel');

/**
 * Renders a <Card> that when clicked, opens the side Modal with the
 * query the tool returned.
 */

@observer
class KaizenToolResultPanel extends Component {
  scrollRef = React.createRef();

  componentDidMount() {
    // Reaction (not componentDidUpdate) since resolvedApprovalTools is only read outside render().
    this.disposeAutoClose = reaction(
      () => this.resolvedApprovalTools,
      (resolvedTools) => {
        const { onCloseToolResult } = this.props;
        if (!onCloseToolResult) {
          return;
        }
        resolvedTools.forEach((tool) => onCloseToolResult(tool));
      }
    );
  }

  componentDidUpdate(prevProps) {
    const { tools } = this.props;
    if (tools?.[0]?.id !== prevProps.tools?.[0]?.id) {
      this.scrollRef.current?.scrollTo({ top: 0 });
    }
  }

  componentWillUnmount() {
    this.disposeAutoClose();
  }

  // Pending-approval tools whose live call (via isSameToolCall) has left AWAITING_APPROVAL; the stale `tools` prop won't reflect it.
  get resolvedApprovalTools() {
    const { tools, sessionModel } = this.props;
    const pendingPreviewTools = (tools || []).filter(
      (tool) =>
        tool?.query_object?._hasApprovalPreview &&
        (tool.status === TOOL_CALL_STATUS.AWAITING_APPROVAL || tool.awaitingApproval)
    );
    // Bail before reading allSteps so the reaction doesn't track the whole session when nothing is pending.
    if (!pendingPreviewTools.length || !sessionModel) {
      return [];
    }
    const liveToolCalls = sessionModel.allSteps.flatMap((step) => step.toolCalls || []);
    return pendingPreviewTools.filter((staleToolRef) => {
      const live = liveToolCalls.find((tc) => isSameToolCall(tc, staleToolRef));
      return !!live?.status && live.status !== TOOL_CALL_STATUS.AWAITING_APPROVAL && !live.awaitingApproval;
    });
  }

  render() {
    const { tools, onCloseToolResult, onDataViewCreate, onQueryError, sessionModel } = this.props;

    if (!tools || tools.length === 0) {
      return null;
    }

    const chartHeight = 500;

    return (
      <ErrorBoundaryCmp>
        <KaizenToolResultPanelWrapper bg="decks.background" ref={this.scrollRef}>
          {tools.map((tool) => {
            // The delegation call gets the full run: instructions, every step, final answer.
            if (isSubAgentTool(tool.tool_name)) {
              return (
                <SubAgentRunPanel
                  key={tool.id}
                  tool={tool}
                  sessionModel={sessionModel}
                  onCloseToolResult={onCloseToolResult}
                />
              );
            }

            return createToolResultComponent(tool, {
              onCloseToolResult: () => onCloseToolResult(tool),
              onDataViewCreate,
              onQueryError,
              chartHeight,
              sessionModel
            });
          })}
        </KaizenToolResultPanelWrapper>
      </ErrorBoundaryCmp>
    );
  }
}

export default KaizenToolResultPanel;
